/* Shop by sound hub: use cases stage (sections/ls-sound-uses.liquid, layout "stage").
 * Tabs + autoplay timer (GSAP tween on the active tab's progress line), GSAP panel transitions and a Three.js
 * point field (assets/ls-three.js = tree-shaken three r186, window.LsThree) that morphs between one look per panel
 * (data-scene = a SCENES key; the shader is built from the scenes the stage uses). Also drives ls-sound-tuning's stage
 * and the single-panel use-case collection hero (ls-use-hero: no tabs, links instead).
 * data-effect="ripple" (use hero + use stage) replaces the point field with a ripple over the panel photos.
 * The per-sound context lines are filled by initUses() in ls-sound.js. */
(() => {
  const roots = document.querySelectorAll('[data-lsu]:not([data-lsu-ready])');
  if (!roots.length) return;

  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fineQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* fn = GLSL body returning the point height at p (ground plane x/z) and time t. */
  const SCENES = {
    gaming: { color: '#5fe3ee', cam: [0, 2.4, 5.6], look: [0, 0, -1.6], fn: `
      vec2 s1 = vec2(cos(t * .32) * 2.4, -.6 + sin(t * .32) * 1.3);
      vec2 s2 = vec2(cos(t * .32 + 3.14) * 2.0, -1.2 + sin(t * .45 + 1.) * 1.0);
      float d1 = length(p - s1);
      float d2 = length(p - s2);
      return sin(d1 * 4.6 - t * 3.0) * exp(-d1 * .42) * .5 + sin(d2 * 5.2 - t * 3.4) * exp(-d2 * .6) * .3;` },
    studio: { color: '#dcdcd6', cam: [0, 4.3, 5.2], look: [0, 0, -2.6], fn: `
      return .05 * sin(p.x * 1.1 + t * .5) + .035 * sin(p.y * 1.7 - t * .35);` },
    travel: { color: '#f2b765', cam: [-1.3, 1.15, 4.7], look: [0.6, 0.1, -3.2], fn: `
      return .3 * sin((p.y + t * 1.5) * 1.45 + sin(p.x * .55 + t * .2) * 1.4) + .1 * sin(p.x * 2.1 - t * .8);` },
    listening: { color: '#b9a4ff', cam: [1.7, 2.1, 4.5], look: [-0.3, 0, -1.9], fn: `
      return .15 * sin(p.x * 2.4 + t * .8) + .13 * sin(p.y * 3.1 - t * .6)
        + .1 * sin((p.x * 1.3 + p.y * 1.9) * 2.0 + t * 1.2)
        + .07 * sin(length(p - vec2(1.5, -1.)) * 7. - t * 2.2);` },
    /* Driver technologies: one moving coil (central piston + long ripples), many small armatures (fine fast cells),
     * a planar membrane (one even wavefront), an electrostatic film (light shimmer), a hybrid (low source + fast source). */
    dynamic: { color: '#ff9a62', cam: [0, 2.6, 5.4], look: [0, 0, -2.2], fn: `
      float d = length(p - vec2(0., -2.4));
      return .34 * exp(-d * d * .14) * sin(t * 2.1) + sin(d * 2.5 - t * 2.4) * exp(-d * .26) * .24;` },
    armature: { color: '#7fd8ff', cam: [0.8, 3.2, 4.8], look: [-0.2, 0, -2.4], fn: `
      vec2 g = fract(p * .85) - .5;
      float d = length(g);
      vec2 id = floor(p * .85);
      float ph = fract(sin(dot(id, vec2(12.9898, 78.233))) * 43758.5453) * 6.28;
      return .1 * sin(d * 20. - t * 6. + ph) * (1. - smoothstep(.08, .5, d)) + .03 * sin(p.x * 2. + t * 1.6);` },
    planar: { color: '#c9f27a', cam: [-1.1, 1.6, 5.0], look: [0.4, 0, -2.8], fn: `
      return .26 * sin(p.y * .75 - t * 1.7) + .025 * sin(p.x * 1.4 + t * .6);` },
    electrostatic: { color: '#e9e4ff', cam: [0, 3.0, 5.0], look: [0, 0, -2.6], fn: `
      return .06 * sin(p.x * 5. + t * 3.2) * sin(p.y * 4. - t * 2.7) + .045 * sin((p.x - p.y) * 9. + t * 5.)
        + .03 * sin(length(p - vec2(-1.5, -3.)) * 12. - t * 6.);` },
    /* Voice calls: a speaking source sending short bursts of rings across a calm field. */
    voice: { color: '#8ff0b8', cam: [0.4, 2.2, 5.2], look: [-0.1, 0, -2.0], fn: `
      float d = length(p - vec2(-1.2, -2.2));
      float burst = .55 + .45 * sin(t * 1.3) * sin(t * .7 + 1.);
      return sin(d * 6.5 - t * 4.2) * exp(-d * .5) * .28 * burst + .03 * sin(p.x * 1.6 + t * .5);` },
    hybrid: { color: '#ffb36b', cam: [0.6, 2.3, 5.2], look: [-0.2, 0, -2.2], fn: `
      float d1 = length(p - vec2(-2., -2.6));
      float d2 = length(p - vec2(2.4, -3.2));
      return sin(d1 * 2.4 - t * 2.2) * exp(-d1 * .3) * .3 + sin(d2 * 8. - t * 5.2) * exp(-d2 * .45) * .16;` },
  };

  const scripts = {};
  const loadScript = (src, ready) => {
    if (ready()) return Promise.resolve();
    if (!scripts[src]) {
      scripts[src] = new Promise((resolve, reject) => {
        const tag = document.createElement('script');
        tag.src = src;
        tag.async = true;
        tag.onload = () => (ready() ? resolve() : reject(new Error('load')));
        tag.onerror = reject;
        document.head.appendChild(tag);
      });
    }
    return scripts[src];
  };
  const loadGsap = (src) => {
    if (window.gsap) return Promise.resolve(window.gsap);
    if (window.lsLoadGsap) return window.lsLoadGsap(src);
    return loadScript(src, () => !!window.gsap).then(() => window.gsap);
  };
  const loadThree = (src) => loadScript(src, () => !!window.LsThree).then(() => window.LsThree);

  const vertexShader = (keys) => {
    const n = keys.length;
    const fns = keys.map((k, i) => `float f${i}(vec2 p, float t) {${SCENES[k].fn}\n    }`).join('\n    ');
    const sum = keys.map((k, i) => `uW[${i}] * f${i}(p, t)`).join(' + ');
    const col = keys.map((k, i) => `uC[${i}] * uW[${i}]`).join(' + ');
    return `
    uniform float uTime;
    uniform float uSize;
    uniform float uW[${n}];
    uniform vec3 uC[${n}];
    attribute float aSeed;
    varying float vH;
    varying float vFog;
    varying float vSeed;
    varying vec3 vCol;
    ${fns}
    void main() {
      vec3 pos = position;
      vec2 p = pos.xz;
      float t = uTime;
      float h = ${sum};
      pos.y = h;
      vec4 mv = modelViewMatrix * vec4(pos, 1.);
      gl_Position = projectionMatrix * mv;
      float depth = -mv.z;
      gl_PointSize = uSize * (1. + max(h, 0.) * 1.2) / depth;
      vH = h;
      vFog = 1. - smoothstep(4., 13., depth);
      vSeed = aSeed;
      vCol = ${col};
    }`;
  };
  const FRAG = `
    varying float vH;
    varying float vFog;
    varying float vSeed;
    varying vec3 vCol;
    void main() {
      vec2 c = gl_PointCoord - .5;
      float r = length(c);
      if (r > .5) discard;
      float a = smoothstep(.5, .12, r);
      float lift = clamp(vH * 1.6 + .45, 0., 1.);
      vec3 col = mix(vec3(.5), vCol, .35 + .65 * lift);
      col = mix(col, vec3(1.), pow(lift, 3.) * .35);
      gl_FragColor = vec4(col, a * vFog * (.3 + .9 * lift) * (.75 + .25 * vSeed));
    }`;

  function makeScene(root, canvas, THREE, reduced, keys) {
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: true, powerPreference: 'high-performance' });
    } catch (e) {
      return null;
    }
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 60);
    const small = window.innerWidth < 750;
    const cols = small ? 120 : 200;
    const rows = small ? 96 : 140;
    const pos = new Float32Array(cols * rows * 3);
    const seed = new Float32Array(cols * rows);
    let i = 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        pos[i * 3] = -8 + (16 * c) / (cols - 1);
        pos[i * 3 + 1] = 0;
        pos[i * 3 + 2] = -9 + (12 * r) / (rows - 1);
        seed[i] = Math.random();
        i++;
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    const colors = keys.map((k) => new THREE.Color(SCENES[k].color));
    const uniforms = {
      uTime: { value: 2 },
      uSize: { value: 15 },
      uW: { value: keys.map((k, i) => (i ? 0 : 1)) },
      uC: { value: colors },
    };
    const mat = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: vertexShader(keys),
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geo, mat);
    points.frustumCulled = false;
    scene.add(points);

    const w = {};
    keys.forEach((k, i) => { w['k' + i] = i ? 0 : 1; });
    const cam = { x: 0, y: 2.4, z: 5.6, lx: 0, ly: 0, lz: -1.6 };
    const look = new THREE.Vector3();
    const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
    let width = 0;
    let height = 0;
    let running = false;
    let raf = 0;
    let last = 0;
    let time = 2;

    const resize = () => {
      const rect = root.getBoundingClientRect();
      width = Math.max(1, Math.round(rect.width));
      height = Math.max(1, Math.round(rect.height));
      const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      renderer.setPixelRatio(dpr);
      renderer.setSize(width, height, false);
      uniforms.uSize.value = 17 * dpr * (width < 750 ? 0.85 : 1);
      camera.aspect = width / height;
      if (width >= 1000) camera.setViewOffset(width, height, -width * 0.17, 0, width, height);
      else camera.clearViewOffset();
      camera.updateProjectionMatrix();
      render();
    };
    const render = () => {
      uniforms.uTime.value = time;
      const v = uniforms.uW.value;
      for (let i = 0; i < v.length; i++) v[i] = w['k' + i];
      pointer.x += (pointer.tx - pointer.x) * 0.04;
      pointer.y += (pointer.ty - pointer.y) * 0.04;
      camera.position.set(cam.x + pointer.x * 0.5, cam.y - pointer.y * 0.25, cam.z);
      look.set(cam.lx, cam.ly, cam.lz);
      camera.lookAt(look);
      renderer.render(scene, camera);
    };
    const frame = (now) => {
      if (!running) return;
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0;
      last = now;
      time += dt;
      render();
      raf = requestAnimationFrame(frame);
    };
    const onMove = (e) => {
      const rect = root.getBoundingClientRect();
      pointer.tx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      pointer.ty = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    if (fineQuery.matches && !reduced) root.addEventListener('pointermove', onMove, { passive: true });
    new ResizeObserver(resize).observe(root);
    resize();

    return {
      go(key, accent, gsap, instant) {
        const idx = Math.max(0, keys.indexOf(key));
        const sc = SCENES[keys[idx]];
        if (accent) colors[idx].set(accent);
        const to = {};
        keys.forEach((k, i) => { to['k' + i] = i === idx ? 1 : 0; });
        const camTo = { x: sc.cam[0], y: sc.cam[1], z: sc.cam[2], lx: sc.look[0], ly: sc.look[1], lz: sc.look[2] };
        if (instant || !gsap) {
          Object.assign(w, to);
          Object.assign(cam, camTo);
          render();
          return;
        }
        gsap.to(w, { ...to, duration: 1.6, ease: 'power2.inOut', overwrite: true });
        gsap.to(cam, { ...camTo, duration: 2.2, ease: 'power3.inOut', overwrite: true });
      },
      play() {
        if (running || reduced) return;
        running = true;
        last = 0;
        raf = requestAnimationFrame(frame);
      },
      pause() {
        running = false;
        cancelAnimationFrame(raf);
      },
      render,
    };
  }

  /* Photo ripple (data-effect="ripple"): the panel photos are drawn by one full-quad shader that bends them with
   * expanding rings, like sound waves on a surface. Rings start where the pointer moves (fine pointers) and from the
   * photo's middle while the page scrolls; panel changes crossfade between photos. Renders only while something
   * moves. The <img> tags stay as the fallback (no WebGL, reduced motion) and need crossorigin="anonymous". */
  const RIPPLES = 10;
  const RIPPLE_LIFE = 2.6;
  const RIPPLE_VERT = `
    varying vec2 vUv;
    void main() {
      vUv = position.xy * .5 + .5;
      gl_Position = vec4(position.xy, 0., 1.);
    }`;
  const RIPPLE_FRAG = `
    uniform sampler2D uTexA;
    uniform sampler2D uTexB;
    uniform vec2 uSizeA;
    uniform vec2 uSizeB;
    uniform float uHasA;
    uniform float uHasB;
    uniform float uMix;
    uniform vec2 uRes;
    uniform vec2 uFocus;
    uniform float uTime;
    uniform vec4 uRip[${RIPPLES}];
    varying vec2 vUv;
    vec2 cover(vec2 uv, vec2 img) {
      float rc = uRes.x / uRes.y;
      float ri = img.x / max(img.y, 1.);
      vec2 s = rc > ri ? vec2(1., ri / rc) : vec2(rc / ri, 1.);
      return uv * s + (1. - s) * uFocus;
    }
    vec3 pick(sampler2D tex, vec2 img, vec2 uv, vec2 disp) {
      float r = texture2D(tex, cover(uv + disp * 1.25, img)).r;
      vec2 gb = texture2D(tex, cover(uv + disp, img)).gb;
      float b = texture2D(tex, cover(uv + disp * .75, img)).b;
      return vec3(r, gb.x, b);
    }
    void main() {
      float aspect = uRes.x / uRes.y;
      vec2 disp = vec2(0.);
      float glow = 0.;
      for (int i = 0; i < ${RIPPLES}; i++) {
        vec4 rp = uRip[i];
        float age = uTime - rp.z;
        if (rp.w <= 0. || age < 0. || age > ${RIPPLE_LIFE.toFixed(1)}) continue;
        vec2 d = (vUv - rp.xy) * vec2(aspect, 1.);
        float dist = length(d);
        float front = dist - age * .34;
        float fade = 1. - age / ${RIPPLE_LIFE.toFixed(1)};
        float env = exp(-front * front * 70.) * fade * fade * rp.w;
        float wave = sin(front * 58.) * env;
        disp += (d / max(dist, .0001)) * wave * vec2(1. / aspect, 1.) * .011;
        glow += max(wave, 0.) * .5;
      }
      vec3 a = pick(uTexA, uSizeA, vUv, disp);
      vec3 b = pick(uTexB, uSizeB, vUv, disp);
      float alpha = mix(uHasA, uHasB, uMix);
      vec3 col = mix(a * uHasA, b * uHasB, uMix);
      col += glow * .07 * alpha;
      gl_FragColor = vec4(col, alpha);
    }`;

  function makeRipple(root, wrap, THREE, imgs) {
    const canvas = document.createElement('canvas');
    canvas.className = 'lsu-ripple';
    canvas.setAttribute('aria-hidden', 'true');
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: true, premultipliedAlpha: false });
    } catch (e) {
      return null;
    }
    wrap.appendChild(canvas);
    const quad = new THREE.BufferGeometry();
    quad.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    const blank = new THREE.Texture();
    const uniforms = {
      uTexA: { value: blank }, uTexB: { value: blank },
      uSizeA: { value: new THREE.Vector2(1, 1) }, uSizeB: { value: new THREE.Vector2(1, 1) },
      uHasA: { value: 0 }, uHasB: { value: 0 }, uMix: { value: 0 },
      uRes: { value: new THREE.Vector2(1, 1) },
      uFocus: { value: new THREE.Vector2(0.5, 0.72) },
      uTime: { value: 0 },
      uRip: { value: Array.from({ length: RIPPLES }, () => new THREE.Vector4(0, 0, -99, 0)) },
    };
    const mesh = new THREE.Mesh(quad, new THREE.ShaderMaterial({ uniforms, vertexShader: RIPPLE_VERT, fragmentShader: RIPPLE_FRAG, depthTest: false, depthWrite: false }));
    mesh.frustumCulled = false;
    const scene = new THREE.Scene();
    scene.add(mesh);
    const camera = new THREE.PerspectiveCamera();

    const textures = new Map();
    const textureOf = (i) => {
      const img = imgs[i];
      if (!img) return Promise.resolve(null);
      if (textures.has(i)) return textures.get(i);
      /* A responsive <img> (srcset) uploads as an empty texture, so load a plain copy sized to the canvas. */
      const need = wrap.getBoundingClientRect().width * Math.min(window.devicePixelRatio || 1, 2);
      const width = [750, 1100, 1500, 1900, 2400].find((x) => x >= need) || 2400;
      const src = (img.currentSrc || img.src).replace(/([?&])width=\d+/, `$1width=${width}`);
      const copy = new Image();
      copy.crossOrigin = 'anonymous';
      copy.decoding = 'async';
      copy.src = src;
      const p = copy.decode()
        .then(() => {
          const tex = new THREE.Texture(copy);
          tex.needsUpdate = true;
          return { tex, w: copy.naturalWidth, h: copy.naturalHeight };
        })
        .catch(() => null);
      textures.set(i, p);
      return p;
    };

    let clock = 0;
    let last = 0;
    let raf = 0;
    let busyUntil = 0;
    let visible = false;
    let slot = 0;
    let current = -1;
    let fade = null;

    const render = () => renderer.render(scene, camera);
    const frame = (now) => {
      raf = 0;
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0;
      last = now;
      clock += dt;
      uniforms.uTime.value = clock;
      render();
      if (visible && (clock < busyUntil || fade)) raf = requestAnimationFrame(frame);
      else last = 0;
    };
    const wake = (seconds) => {
      busyUntil = Math.max(busyUntil, clock + seconds);
      if (!raf && visible) raf = requestAnimationFrame(frame);
    };
    const emit = (x, y, strength) => {
      uniforms.uRip.value[slot].set(x, y, clock, strength);
      slot = (slot + 1) % RIPPLES;
      wake(RIPPLE_LIFE);
    };
    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(w, h, false);
      uniforms.uRes.value.set(w, h);
      render();
    };
    const pos = getComputedStyle(imgs.find(Boolean) || wrap).objectPosition.split(' ');
    const px = parseFloat(pos[0]);
    const py = parseFloat(pos[1]);
    uniforms.uFocus.value.set(Number.isFinite(px) ? px / 100 : 0.5, 1 - (Number.isFinite(py) ? py / 100 : 0.5));

    let lastX = null;
    let lastY = null;
    let lastT = 0;
    let travel = 0;
    if (fineQuery.matches) {
      root.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        const rect = wrap.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        if (lastX !== null) travel += Math.hypot(x - lastX, y - lastY);
        lastX = x;
        lastY = y;
        const now = performance.now();
        if (travel < 46 || now - lastT < 70) return;
        const speed = travel / Math.max(now - lastT, 16);
        travel = 0;
        lastT = now;
        if (x < -rect.width * 0.1 || x > rect.width * 1.1 || y < 0 || y > rect.height) return;
        emit(x / rect.width, 1 - y / rect.height, Math.min(1, 0.35 + speed * 0.45));
      }, { passive: true });
      root.addEventListener('pointerleave', () => { lastX = null; lastY = null; travel = 0; });
    }
    let lastScroll = window.scrollY;
    let scrolled = 0;
    let scrollT = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      scrolled += Math.abs(y - lastScroll);
      lastScroll = y;
      if (!visible) return;
      const now = performance.now();
      if (scrolled < 90 || now - scrollT < 160) return;
      const strength = Math.min(1, 0.3 + scrolled / 420);
      scrolled = 0;
      scrollT = now;
      emit(0.42 + Math.random() * 0.2, 0.42 + Math.random() * 0.24, strength);
    }, { passive: true });

    new ResizeObserver(resize).observe(wrap);
    resize();

    return {
      show(i, gsap, instant) {
        if (i === current) return Promise.resolve();
        current = i;
        return textureOf(i).then((t) => {
          if (current !== i) return;
          if (imgs[i + 1]) textureOf(i + 1);
          if (fade) { fade.kill(); fade = null; }
          const first = !uniforms.uHasA.value && !uniforms.uHasB.value;
          uniforms.uTexB.value = t ? t.tex : blank;
          uniforms.uSizeB.value.set(t ? t.w : 1, t ? t.h : 1);
          uniforms.uHasB.value = t ? 1 : 0;
          const settle = () => {
            uniforms.uTexA.value = uniforms.uTexB.value;
            uniforms.uSizeA.value.copy(uniforms.uSizeB.value);
            uniforms.uHasA.value = uniforms.uHasB.value;
            uniforms.uMix.value = 0;
            fade = null;
            render();
          };
          if (instant || first || !gsap) { settle(); return !!t; }
          uniforms.uMix.value = 0;
          fade = gsap.to(uniforms.uMix, { value: 1, duration: 1.1, ease: 'power2.inOut', onComplete: settle });
          emit(0.5, 0.5, 0.8);
          return !!t;
        });
      },
      play() {
        visible = true;
        wake(0.05);
      },
      pause() {
        visible = false;
        cancelAnimationFrame(raf);
        raf = 0;
        last = 0;
      },
    };
  }

  function init(root) {
    root.dataset.lsuReady = '1';
    const tabs = [...root.querySelectorAll('[role=tab]')];
    const panels = tabs.length
      ? tabs.map((t) => document.getElementById(t.getAttribute('aria-controls')))
      : [...root.querySelectorAll('.lsu-panel')];
    if (!panels.length || panels.some((p) => !p)) return;
    const show = (p, on) => {
      p.toggleAttribute('data-off', !on);
      p.inert = !on;
    };
    const toggle = root.querySelector('[data-lsu-toggle]');
    const canvas = root.querySelector('.lsu-canvas');
    const effect = root.dataset.effect || (root.dataset.sceneOn === 'true' ? 'points' : 'none');
    const photoWrap = root.querySelector('[data-lsu-photos]');
    const photoImgs = panels.map((p, i) => (photoWrap ? photoWrap.querySelector(`[data-lsu-img="${i}"] img`) : null));
    let ripple = null;
    const showPhoto = (i) => {
      photoImgs.forEach((img, n) => { if (img) img.classList.toggle('is-on', n === i); });
      if (photoWrap) photoWrap.classList.toggle('is-empty', !photoImgs[i]);
    };
    const interval = Math.max(2, parseFloat(root.dataset.interval) || 7);
    const reduced = reduceQuery.matches;
    const editor = root.hasAttribute('data-design-mode');
    const autoplay = root.dataset.autoplay === 'true' && tabs.length > 1;

    let gsap = null;
    let stage = null;
    let index = 0;
    let timer = null;
    let swap = null;
    let userPaused = reduced || editor;
    let hoverPaused = false;
    let focusPaused = false;
    let inView = false;

    const sceneOf = (panel) => (SCENES[panel.dataset.scene] ? panel.dataset.scene : 'gaming');
    const accentOf = (panel) => panel.dataset.accent || SCENES[sceneOf(panel)].color;
    const fillOf = (i) => tabs[i].querySelector('.lsu-tab-fill');
    const setAccent = (panel) => root.style.setProperty('--lsu-accent', accentOf(panel));
    setAccent(panels[0]);

    const shouldRun = () => autoplay && !userPaused && !hoverPaused && !focusPaused && inView && !document.hidden;
    const syncToggle = () => {
      if (!toggle) return;
      const paused = userPaused;
      root.classList.toggle('is-paused', paused);
      toggle.setAttribute('aria-label', paused ? root.dataset.playLabel : root.dataset.pauseLabel);
    };
    const syncTimer = () => {
      if (!timer) return;
      if (shouldRun()) timer.resume();
      else timer.pause();
    };
    const startTimer = () => {
      if (timer) timer.kill();
      timer = null;
      tabs.forEach((t, i) => { if (i !== index) gsap.set(fillOf(i), { scaleX: 0 }); });
      if (!autoplay) return;
      timer = gsap.fromTo(fillOf(index), { scaleX: 0 }, {
        scaleX: 1,
        duration: interval,
        ease: 'none',
        paused: true,
        onComplete: () => select((index + 1) % tabs.length, false),
      });
      syncTimer();
    };

    const select = (next, fromUser) => {
      if (next === index && timer) return;
      const prev = panels[index];
      const target = panels[next];
      tabs.forEach((t, i) => {
        const on = i === next;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        t.classList.toggle('is-active', on);
      });
      index = next;
      setAccent(target);
      const bar = tabs[next].parentElement;
      if (bar.scrollWidth > bar.clientWidth + 1) {
        const tr = tabs[next].getBoundingClientRect();
        const left = bar.scrollLeft + tr.left - bar.getBoundingClientRect().left - (bar.clientWidth - tr.width) / 2;
        bar.scrollTo({ left: Math.max(0, left), behavior: reduced ? 'auto' : 'smooth' });
      }
      if (stage) stage.go(sceneOf(target), target.dataset.accent, gsap, reduced);
      showPhoto(next);
      if (ripple) ripple.show(next, gsap, reduced);

      if (gsap && !reduced && prev !== target) {
        if (swap) swap.progress(1).kill();
        panels.forEach((p) => { if (p !== prev && p !== target) show(p, false); });
        const items = target.querySelectorAll('[data-lsu-in]');
        gsap.set(items, { autoAlpha: 0, y: 24 });
        show(target, true);
        swap = gsap.timeline({ onComplete: () => { swap = null; } })
          .to(prev, { autoAlpha: 0, y: -12, duration: 0.28, ease: 'power2.out', onComplete: () => { show(prev, false); gsap.set(prev, { clearProps: 'opacity,visibility,transform' }); } })
          .to(items, { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.06, clearProps: 'opacity,visibility,transform' }, 0.16);
      } else {
        panels.forEach((p) => show(p, p === target));
      }
      if (gsap) startTimer();
      if (fromUser === 'key') tabs[next].focus();
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => select(i, true));
      tab.addEventListener('keydown', (e) => {
        let n = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = tabs.length - 1;
        if (n === null) return;
        e.preventDefault();
        select(n, 'key');
      });
    });
    const step = (d) => select((index + d + tabs.length) % tabs.length, true);
    const prevBtn = root.querySelector('[data-lsu-prev]');
    const nextBtn = root.querySelector('[data-lsu-next]');
    if (prevBtn) prevBtn.addEventListener('click', () => step(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => step(1));
    if (toggle) {
      toggle.addEventListener('click', () => {
        userPaused = !userPaused;
        syncToggle();
        if (!userPaused && timer && timer.progress() >= 1) startTimer();
        syncTimer();
      });
    }
    const content = root.querySelector('.lsu-panels');
    content.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hoverPaused = true; syncTimer(); } });
    content.addEventListener('pointerleave', () => { hoverPaused = false; syncTimer(); });
    root.addEventListener('focusin', (e) => {
      if (e.target === toggle) return;
      let visible = false;
      try { visible = e.target.matches(':focus-visible'); } catch (err) { visible = false; }
      focusPaused = visible;
      syncTimer();
    });
    root.addEventListener('focusout', (e) => {
      if (root.contains(e.relatedTarget)) return;
      focusPaused = false;
      syncTimer();
    });
    document.addEventListener('visibilitychange', () => {
      syncTimer();
      if (!stage) return;
      if (document.hidden || !inView) stage.pause();
      else stage.play();
    });

    new IntersectionObserver((entries) => {
      inView = entries[0].isIntersecting;
      syncTimer();
      if (stage) {
        if (inView && !document.hidden) stage.play();
        else stage.pause();
      }
    }, { threshold: 0.35 }).observe(root);
    new IntersectionObserver((entries) => {
      const on = entries[0].isIntersecting && !document.hidden;
      if (ripple) ripple[on ? 'play' : 'pause']();
    }).observe(root);
    document.addEventListener('visibilitychange', () => {
      if (ripple) ripple[document.hidden ? 'pause' : 'play']();
    });

    syncToggle();
    const boot = () => {
      loadGsap(root.dataset.gsapSrc).then((g) => {
        gsap = g;
        root.classList.add('is-timed');
        startTimer();
      }).catch(() => {});
      if (effect === 'ripple') {
        if (reduced || !photoWrap || !photoImgs.some(Boolean)) return;
        loadThree(root.dataset.threeSrc).then((THREE) => {
          ripple = makeRipple(root, photoWrap, THREE, photoImgs);
          if (!ripple) return;
          return ripple.show(index, null, true).then((ok) => {
            if (!ok) { ripple.pause(); ripple = null; return; }
            photoWrap.classList.add('is-rippling');
            const r = root.getBoundingClientRect();
            if (r.bottom > 0 && r.top < window.innerHeight && !document.hidden) ripple.play();
          });
        }).catch(() => {});
        return;
      }
      if (effect !== 'points' || !canvas) return;
      loadThree(root.dataset.threeSrc).then((THREE) => {
        stage = makeScene(root, canvas, THREE, reduced, [...new Set(panels.map(sceneOf))]);
        if (!stage) return;
        root.classList.add('is-gl');
        stage.go(sceneOf(panels[index]), panels[index].dataset.accent, null, true);
        if (inView && !document.hidden) stage.play();
      }).catch(() => {});
    };
    const near = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      near.disconnect();
      boot();
    }, { rootMargin: '900px 0px' });
    near.observe(root);

    if (editor) {
      document.addEventListener('shopify:block:select', (e) => {
        const i = panels.findIndex((p) => p.contains(e.target) || p === e.target);
        if (i > -1) select(i, true);
      });
    }
  }

  roots.forEach(init);
})();
